import {useCallback, useMemo, useRef, useState} from 'react'
import {ActivityIndicator, FlatList, View} from 'react-native'
import Animated, {
  LayoutAnimationConfig,
  LinearTransition,
  ZoomIn,
  ZoomOut,
} from 'react-native-reanimated'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useFocusEffect} from '@react-navigation/native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'
import {type ImageInfo} from 'matrix-js-sdk/lib/@types/media'

import {EMOJI_REACTION_LIMIT} from '#/lib/constants'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {isWeb} from '#/platform/detection'
import {useLightboxControls} from '#/state/lightbox'
import {useMatrixClient} from '#/state/matrix/hooks/useMatrixClient'
import {
  type MatrixMessage,
  useMatrixRoom,
} from '#/state/matrix/hooks/useMatrixRoom'
import {mxcToHttpDownload} from '#/state/matrix/media'
import {type AttachmentToPick} from '#/state/matrix/types'
import {processUploadQueue} from '#/state/matrix/uploadQueue'
import {useProfileQuery} from '#/state/queries/profile'
import {
  EmojiPicker,
  type EmojiPickerState,
} from '#/view/com/composer/text-input/web/EmojiPicker'
import * as Toast from '#/view/com/util/Toast'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, native, useTheme, web} from '#/alf'
import * as Dialog from '#/components/Dialog'
import {SecureAttachmentBubble} from '#/components/dms/SecureAttachmentBubble'
import {SecureChatActionsWrapper} from '#/components/dms/SecureChatActionsWrapper'
import {SecureChatCallButton} from '#/components/dms/SecureChatCallButton'
import {SecureChatDropTarget} from '#/components/dms/SecureChatDropTarget'
import {SecureChatMenu} from '#/components/dms/SecureChatMenu'
import {SecureChatMessageInput} from '#/components/dms/SecureChatMessageInput'
import {SecureUploadConfirmDialog} from '#/components/dms/SecureUploadConfirmDialog'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'SecureChat'>

/** Format timestamp for message metadata */
function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Format date for day dividers */
function formatDateDivider(ts: number): string {
  const date = new Date(ts)
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === now.toDateString()) {
    return `Today`
  } else if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday`
  } else {
    return date.toLocaleDateString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }
}

type RenderItem =
  | {
      type: 'message'
      message: MatrixMessage
      isLastInGroup: boolean
      needsTail: boolean
    }
  | {type: 'date-divider'; date: string; key: string}

export function SecureChatScreen({route, navigation}: Props) {
  const {roomId} = route.params
  const {_} = useLingui()
  const t = useTheme()
  const {
    messages,
    members,
    myUserId,
    isLoading,
    isLocalRoom,
    sendMessage,
    sendAttachment,
    sendReaction,
    removeReaction,
    markAsRead,
    loadEarlierMessages,
    promoteLocalRoom,
  } = useMatrixRoom(roomId)
  const {client} = useMatrixClient()
  const {openLightbox} = useLightboxControls()
  const flatListRef = useRef<FlatList>(null)

  // Upload confirmation dialog
  const uploadConfirmControl = Dialog.useDialogControl()
  const [pendingAttachments, setPendingAttachments] = useState<
    AttachmentToPick[]
  >([])

  // Pending upload messages (optimistic echo)
  const [pendingMessages, setPendingMessages] = useState<MatrixMessage[]>([])

  // Combined messages (real + pending)
  const allMessages = useMemo(() => {
    return [...messages, ...pendingMessages]
  }, [messages, pendingMessages])

  useFocusEffect(
    useCallback(() => {
      markAsRead()
    }, [markAsRead]),
  )

  // Web emoji picker state
  const [emojiPickerState, setEmojiPickerState] = useState<EmojiPickerState>({
    isOpen: false,
    pos: {top: 0, left: 0, right: 0, bottom: 0, nextFocusRef: null},
  })

  const onEmojiSelect = useCallback(
    (message: MatrixMessage, emoji: string) => {
      const existingReaction = message.reactions.find(
        r => r.emoji === emoji && r.senderUserId === myUserId,
      )
      if (existingReaction) {
        removeReaction(existingReaction.eventId)
      } else {
        const myReactionCount = message.reactions.filter(
          r => r.senderUserId === myUserId,
        ).length
        if (myReactionCount >= EMOJI_REACTION_LIMIT) return
        sendReaction(message.eventId, emoji)
      }
    },
    [myUserId, sendReaction, removeReaction],
  )

  // Handle file pick from input component (picker, paste, drag-and-drop)
  const onPickAttachments = useCallback(
    (attachments: AttachmentToPick[]) => {
      setPendingAttachments(attachments)
      uploadConfirmControl.open()
    },
    [uploadConfirmControl],
  )

  // Handle confirmed upload
  const onConfirmUpload = useCallback(
    async (attachments: AttachmentToPick[]) => {
      console.log(
        '[SecureChat] onConfirmUpload called, client:',
        !!client,
        'attachments:',
        attachments.length,
      )
      if (!client) {
        console.error('[SecureChat] No client available for upload')
        return
      }

      // Handle local room promotion if needed
      if (isLocalRoom) {
        try {
          const newRoomId = await promoteLocalRoom('')
          if (newRoomId) {
            navigation.replace('SecureChat', {roomId: newRoomId})
          }
        } catch {
          Toast.show(_(msg`Failed to create chat room`), 'xmark')
          return
        }
      }

      // Insert optimistic pending messages
      const newPending: MatrixMessage[] = attachments.map((att, i) => ({
        eventId: `pending-${Date.now()}-${i}`,
        sender: myUserId,
        senderName: 'You',
        body: att.name,
        timestamp: Date.now() + i,
        type: att.mime.startsWith('image/') ? 'm.image' : 'm.file',
        isMe: true,
        reactions: [],
        attachment: {
          msgtype: att.mime.startsWith('image/')
            ? 'm.image'
            : att.mime.startsWith('video/')
            ? 'm.video'
            : 'm.file',
          info: {
            mimetype: att.mime,
            size: att.size,
            w: att.width,
            h: att.height,
          } as ImageInfo,
          filename: att.name,
        },
        sendStatus: 'sending' as const,
        localUri: att.uri,
      }))

      setPendingMessages(prev => [...prev, ...newPending])

      // Scroll to bottom
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({animated: true})
      }, 100)

      // Process upload queue
      await processUploadQueue(client, attachments, {
        onStatusChange: (_id, status) => {
          setPendingMessages(prev =>
            prev.map(m =>
              m.sendStatus === 'sending'
                ? {
                    ...m,
                    sendStatus:
                      status === 'sent'
                        ? ('sent' as const)
                        : ('sending' as const),
                  }
                : m,
            ),
          )
        },
        onComplete: () => {
          setPendingMessages(prev => prev.filter(m => m.sendStatus !== 'sent'))
        },
        onError: (_id, error) => {
          console.error('[SecureChat] Upload error:', error)
          setPendingMessages(prev =>
            prev.map(m =>
              m.sendStatus === 'sending'
                ? {...m, sendStatus: 'failed' as const}
                : m,
            ),
          )
          Toast.show(_(msg`Upload failed: ${error}`), 'xmark')
        },
        sendAttachment,
      })

      // Clean up completed pending messages
      setPendingMessages(prev => prev.filter(m => m.sendStatus !== 'sent'))
    },
    [
      client,
      isLocalRoom,
      myUserId,
      sendAttachment,
      promoteLocalRoom,
      navigation,
      _,
    ],
  )

  // Open lightbox for image attachment
  const onImagePress = useCallback(
    (message: MatrixMessage) => {
      if (!message.attachment || message.attachment.msgtype !== 'm.image')
        return

      let uri: string | null = null
      if (message.localUri) {
        uri = message.localUri
      } else if (client && message.attachment.url) {
        uri = mxcToHttpDownload(client, message.attachment.url)
      }
      if (!uri) return

      const info = message.attachment.info as ImageInfo | undefined
      openLightbox({
        images: [
          {
            uri,
            dimensions:
              info?.w && info?.h ? {width: info.w, height: info.h} : null,
            thumbUri: uri,
            thumbDimensions: null,
            thumbRect: null,
            alt: message.attachment.filename,
            type: 'image',
          },
        ],
        index: 0,
      })
    },
    [client, openLightbox],
  )

  // Find the other member's DID for profile lookup
  const otherMember = members.find(m => m.userId !== myUserId)
  const otherDid = otherMember?.did ?? undefined
  const {data: otherProfile} = useProfileQuery({did: otherDid})

  const displayName =
    otherProfile?.displayName ||
    otherProfile?.handle ||
    otherMember?.displayName ||
    ''
  const handle = otherProfile?.handle ? `@${otherProfile.handle}` : ''

  const onSend = useCallback(
    async (text: string) => {
      if (isLocalRoom) {
        // Promote local room to real room on first message
        const newRoomId = await promoteLocalRoom(text)
        if (newRoomId) {
          // Replace the current route with the real room
          navigation.replace('SecureChat', {roomId: newRoomId})
        }
        return
      }
      await sendMessage(text)
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({animated: true})
      }, 100)
    },
    [isLocalRoom, sendMessage, promoteLocalRoom, navigation],
  )

  // Build render items with date dividers and grouping
  const renderItems = useMemo((): RenderItem[] => {
    const items: RenderItem[] = []
    for (let i = 0; i < allMessages.length; i++) {
      const cur = allMessages[i]
      const prevMsg = i > 0 ? allMessages[i - 1] : null
      const nextMsg = i < allMessages.length - 1 ? allMessages[i + 1] : null

      // Date divider
      const msgDate = new Date(cur.timestamp).toDateString()
      const prevDate = prevMsg
        ? new Date(prevMsg.timestamp).toDateString()
        : null
      if (msgDate !== prevDate) {
        items.push({
          type: 'date-divider',
          date: formatDateDivider(cur.timestamp),
          key: `date-${msgDate}`,
        })
      }

      const isNextFromSameSender = nextMsg?.sender === cur.sender
      const isNextSameDay = nextMsg
        ? new Date(nextMsg.timestamp).toDateString() === msgDate
        : false
      const needsTail = !isNextFromSameSender || !isNextSameDay
      const isLastInGroup = needsTail

      items.push({
        type: 'message',
        message: cur,
        isLastInGroup,
        needsTail,
      })
    }
    return items
  }, [allMessages])

  const renderItem = useCallback(
    ({item}: {item: RenderItem}) => {
      if (item.type === 'date-divider') {
        return (
          <View style={[a.w_full, a.my_lg, a.align_center]}>
            <Text
              style={[a.text_xs, t.atoms.text_contrast_medium, a.text_center]}>
              {item.date}
            </Text>
          </View>
        )
      }

      const {message, isLastInGroup, needsTail} = item
      const isFromSelf = message.isMe

      const reactions = message.reactions
      const hasReactions = reactions.length > 0

      return (
        <View
          style={[
            a.px_lg,
            a.my_2xs,
            isFromSelf ? a.mr_md : a.ml_md,
            needsTail && !isFromSelf && !hasReactions && a.mb_md,
            needsTail && isFromSelf && !hasReactions && a.mb_md,
          ]}>
          <SecureChatActionsWrapper
            message={message}
            myUserId={myUserId}
            isFromSelf={isFromSelf}
            onEmojiSelect={emoji => onEmojiSelect(message, emoji)}>
            {message.attachment ? (
              <View
                style={[
                  a.overflow_hidden,
                  {
                    borderRadius: 17,
                  },
                  isFromSelf
                    ? {borderBottomRightRadius: needsTail ? 2 : 17}
                    : {borderBottomLeftRadius: needsTail ? 2 : 17},
                  message.sendStatus === 'sending' && {opacity: 0.7},
                ]}>
                <SecureAttachmentBubble
                  attachment={message.attachment}
                  localUri={message.localUri}
                  client={client}
                  onPress={() => onImagePress(message)}
                />
                {message.sendStatus === 'sending' && (
                  <View
                    style={[
                      a.absolute,
                      a.inset_0,
                      a.align_center,
                      a.justify_center,
                    ]}>
                    <ActivityIndicator color={t.palette.white} />
                  </View>
                )}
                {message.sendStatus === 'failed' && (
                  <View
                    style={[
                      a.absolute,
                      {bottom: 4, right: 4},
                      a.rounded_full,
                      a.px_sm,
                      a.py_2xs,
                      {backgroundColor: 'rgba(220,38,38,0.9)'},
                    ]}>
                    <Text style={[a.text_xs, {color: 'white'}]}>
                      <Trans>Failed</Trans>
                    </Text>
                  </View>
                )}
              </View>
            ) : (
              <View
                style={[
                  a.py_sm,
                  {
                    paddingLeft: 14,
                    paddingRight: 14,
                    backgroundColor: isFromSelf
                      ? t.palette.primary_500
                      : t.palette.contrast_50,
                    borderRadius: 17,
                  },
                  isFromSelf
                    ? {borderBottomRightRadius: needsTail ? 2 : 17}
                    : {borderBottomLeftRadius: needsTail ? 2 : 17},
                ]}>
                <Text
                  style={[a.text_md, isFromSelf && {color: t.palette.white}]}>
                  {message.body}
                </Text>
              </View>
            )}
            <LayoutAnimationConfig skipEntering skipExiting>
              {hasReactions && (
                <View
                  style={[
                    isFromSelf ? a.align_end : a.align_start,
                    a.px_sm,
                    a.pb_2xs,
                  ]}>
                  <View
                    style={[
                      a.flex_row,
                      a.gap_2xs,
                      a.py_xs,
                      a.px_xs,
                      a.justify_center,
                      isFromSelf ? a.justify_end : a.justify_start,
                      a.flex_wrap,
                      a.pb_xs,
                      t.atoms.bg_contrast_25,
                      a.border,
                      t.atoms.border_contrast_low,
                      a.rounded_lg,
                      t.atoms.shadow_sm,
                      {transform: [{translateY: -11}]},
                    ]}>
                    {reactions.map((reaction, _i, allReactions) => {
                      const label =
                        reaction.senderUserId === myUserId
                          ? _(msg`You reacted ${reaction.emoji}`)
                          : _(
                              msg`${reaction.senderName} reacted ${reaction.emoji}`,
                            )
                      return (
                        <Animated.View
                          entering={native(ZoomIn.springify(200).delay(400))}
                          exiting={
                            allReactions.length > 1 &&
                            native(ZoomOut.delay(200))
                          }
                          layout={native(LinearTransition.delay(300))}
                          key={reaction.senderUserId + reaction.emoji}
                          style={[a.p_2xs]}
                          accessible={true}
                          accessibilityLabel={label}
                          accessibilityHint={_(
                            msg`Double tap or long press the message to add a reaction`,
                          )}>
                          <Text emoji style={[a.text_sm]}>
                            {reaction.emoji}
                          </Text>
                        </Animated.View>
                      )
                    })}
                  </View>
                </View>
              )}
            </LayoutAnimationConfig>
          </SecureChatActionsWrapper>
          {isLastInGroup && (
            <Text
              style={[
                a.text_xs,
                t.atoms.text_contrast_medium,
                a.mt_2xs,
                a.mb_lg,
                isFromSelf ? a.text_right : a.text_left,
              ]}>
              {formatTime(message.timestamp)}
            </Text>
          )}
        </View>
      )
    },
    [t, _, myUserId, onEmojiSelect, client, onImagePress],
  )

  if (isLoading) {
    return (
      <Layout.Screen>
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              {_(msg`Loading...`)}
            </Layout.Header.TitleText>
          </Layout.Header.Content>
        </Layout.Header.Outer>
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen>
      {/* Header with profile info */}
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <View style={[a.flex_row, a.align_start, a.gap_md, a.flex_1, a.pr_md]}>
          <View style={[a.pt_2xs]}>
            <UserAvatar size={isWeb ? 40 : 34} avatar={otherProfile?.avatar} />
          </View>
          <View style={[a.flex_1]}>
            <Text
              numberOfLines={1}
              style={[
                a.text_md,
                a.font_bold,
                a.self_start,
                web(a.leading_normal),
              ]}>
              {displayName}
            </Text>
            {handle ? (
              <Text
                numberOfLines={1}
                style={[
                  t.atoms.text_contrast_medium,
                  a.text_sm,
                  web([a.leading_normal, {marginTop: -2}]),
                ]}>
                {handle}
              </Text>
            ) : null}
          </View>
        </View>
        <SecureChatCallButton roomId={roomId} />
        {otherProfile && (
          <SecureChatMenu roomId={roomId} profile={otherProfile} />
        )}
      </Layout.Header.Outer>

      <Layout.Center style={[a.flex_1]}>
        {isWeb ? (
          <SecureChatDropTarget onDropFiles={onPickAttachments}>
            <FlatList
              ref={flatListRef}
              data={renderItems}
              renderItem={renderItem}
              keyExtractor={item =>
                item.type === 'date-divider' ? item.key : item.message.eventId
              }
              onEndReached={loadEarlierMessages}
              onEndReachedThreshold={0.5}
              style={[a.flex_1]}
            />
          </SecureChatDropTarget>
        ) : (
          <FlatList
            ref={flatListRef}
            data={renderItems}
            renderItem={renderItem}
            keyExtractor={item =>
              item.type === 'date-divider' ? item.key : item.message.eventId
            }
            onEndReached={loadEarlierMessages}
            onEndReachedThreshold={0.5}
            style={[a.flex_1]}
          />
        )}

        <SecureChatMessageInput
          onSendMessage={onSend}
          onPickAttachments={onPickAttachments}
          openEmojiPicker={pos => setEmojiPickerState({isOpen: true, pos})}
        />
      </Layout.Center>

      {isWeb && (
        <EmojiPicker
          pinToTop
          state={emojiPickerState}
          close={() => setEmojiPickerState(prev => ({...prev, isOpen: false}))}
        />
      )}

      <SecureUploadConfirmDialog
        control={uploadConfirmControl}
        attachments={pendingAttachments}
        onConfirm={onConfirmUpload}
        onCancel={() => setPendingAttachments([])}
      />
    </Layout.Screen>
  )
}
